import React, { useEffect, useState } from "react";
import { List, FormInstance } from "antd";
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { updateAppearanceConfig } from "store/slices/kiosksSlice";
import { UpdateAppearanceConfigPayload } from "types/kioskTypes";
import { hasPermission } from "utils/utils";
import { patchRequest } from "api/apiClient";
import { Location } from "types/locationTypes";
import { useTranslation } from "react-i18next";

interface OptionsContentProps {
  optionsForm: FormInstance;
  defaultLocationId: number | undefined;
  locations: Location[];
  kioskLocations: Location[];
  isMultiLocationsOn: boolean;
  isMultiLocationsOnInitially: boolean;
  setIsMultiLocationsOn: (check: boolean) => void;
  setIsFieldsTouched: (check: boolean) => void;
}

const OptionsContent: React.FC<OptionsContentProps> = ({
  optionsForm,
  defaultLocationId,
  locations,
  kioskLocations,
  isMultiLocationsOn,
  isMultiLocationsOnInitially,
  setIsMultiLocationsOn,
  setIsFieldsTouched,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
  const selectedKiosk = useSelector(
    (state: RootState) => state.kiosks.selectedKiosk,
  );
  const tokenScopeList = useSelector(
    (state: RootState) => state.auth.auth.data[0]?.tokenScopeList || [],
  );

  const hasKioskRead = hasPermission(tokenScopeList, orgId, "o", "kiosk:r");
  const hasKioskWrite = hasPermission(tokenScopeList, orgId, "o", "kiosk:w");

  const [isScanIdCameraDisabled, setScanIdCameraDisabled] = useState(true);

  const { t } = useTranslation();

  // Define initial values as a constant
  useEffect(() => {
    if (selectedKiosk) {
      optionsForm.setFieldsValue({
        allowSignOut: selectedKiosk?.appearance[0]?.allowSignOut || false,
        scanQR: selectedKiosk?.appearance[0]?.scanQR || false,
        scanID: selectedKiosk?.appearance[0]?.scanID || false,
        scanIdCamera: selectedKiosk?.appearance[0]?.scanIdCamera || false,
        badgePrinter: selectedKiosk?.appearance[0]?.badgePrinter || false,
        selfSignInsQR: selectedKiosk?.appearance[0]?.selfSignInsQR || false,
        allowMultiLocation: isMultiLocationsOnInitially || false,
        locations: kioskLocations
          .filter((location) => location.id !== defaultLocationId)
          .map((location) => location.id),
      });
    }
    // eslint-disable-next-line
  }, [selectedKiosk, kioskLocations, optionsForm, isMultiLocationsOnInitially]);

  useEffect(() => {
    const initialScanIdState = selectedKiosk?.appearance[0].scanID;
    setScanIdCameraDisabled(!initialScanIdState);
    if (!initialScanIdState) {
      optionsForm.setFieldsValue({ scanIdCamera: false });
    }
  }, [selectedKiosk, optionsForm]);

  const handleSubmit = async (args: IOnSubmitArgs) => {
    if (!orgId) {
      return;
    }

    const { values } = args;
    const payload: Partial<UpdateAppearanceConfigPayload> = {
      allowSignOut: values.allowSignOut ? 1 : 0,
      scanQR: values.scanQR ? 1 : 0,
      scanID: values.scanID ? 1 : 0,
      scanIdCamera: values.scanIdCamera ? 1 : 0,
      badgePrinter: values.badgePrinter ? 1 : 0,
      selfSignInsQR: values.selfSignInsQR ? 1 : 0,
    };
    if (selectedKiosk) {
      await dispatch(
        updateAppearanceConfig({
          orgId,
          kioskId: selectedKiosk.id,
          appearanceConfig: payload,
        }),
      );
      if (isMultiLocationsOn && values.locations.length > 0) {
        await patchRequest(`/orgs/${orgId}/kiosk/${selectedKiosk.id}/sites`, {
          siteId: values.locations,
        });
      } else {
        await patchRequest(`/orgs/${orgId}/kiosk/${selectedKiosk.id}/sites`, {
          siteId: [],
        });
      }
    }
  };

  const handleScanIdChange = (checked: boolean) => {
    setScanIdCameraDisabled(!checked);
    if (!checked) {
      optionsForm.setFieldsValue({ scanIdCamera: false });
    }
  };

  const handleToggleAllowMultiLocation = (checked: boolean) => {
    // Update the form value directly
    optionsForm.setFieldsValue({ allowMultiLocation: checked });

    // Trigger any additional state updates
    setIsMultiLocationsOn(checked);
    setIsFieldsTouched(true);
  };

  return (
    <>
      <OpForm
        form={optionsForm}
        onSubmit={handleSubmit}
        hasError={false}
        defaultButtons={false}
        isReadOnly={!hasKioskWrite && hasKioskRead}
      >
        <List
          header={t("kiosk.form.optionsTitle")}
          dataSource={[
            {
              name: "allowSignOut",
              label: t("kiosk.form.allowSignOut"),
              valuePropName: "checked",
            },
            {
              name: "scanQR",
              label: t("kiosk.form.scanQR"),
              valuePropName: "checked",
            },
            {
              name: "scanID",
              label: t("kiosk.form.scanID"),
              valuePropName: "checked",
            },
            {
              name: "scanIdCamera",
              label: t("kiosk.form.scanIdCamera"),
              valuePropName: "checked",
              dependent: true,
            },
            {
              name: "selfSignInsQR",
              label: t("kiosk.form.selfSignInsQR"),
              valuePropName: "checked",
            },
            {
              name: "badgePrinter",
              label: t("kiosk.form.badgePrinter"),
              valuePropName: "checked",
            },
          ]}
          bordered
          renderItem={(item) => (
            <List.Item>
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <OpForm.Switch
                  name={item.name}
                  style={{ marginBottom: 0 }}
                  disabled={
                    item.dependent &&
                    (isScanIdCameraDisabled || (!hasKioskWrite && hasKioskRead))
                  }
                  onChange={
                    item.name === "scanID" ? handleScanIdChange : undefined
                  }
                />
                <span style={{ marginLeft: 8 }}>{item.label}</span>
              </div>
            </List.Item>
          )}
        />

        <List
          header={t("kiosk.form.multiLocationTitle")}
          dataSource={[
            {
              name: "allowMultiLocation",
              label: t("kiosk.form.toggle"),
              valuePropName: "checked",
            },
          ]}
          bordered
          renderItem={(item) => (
            <List.Item>
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <OpForm.Item
                  name={item.name}
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <OpForm.Switch onChange={handleToggleAllowMultiLocation} />
                </OpForm.Item>
                <span style={{ flex: 1, marginLeft: 8 }}>{item.label}</span>
              </div>
            </List.Item>
          )}
          style={{ marginTop: "24px" }}
        >
          {isMultiLocationsOn && (
            <List.Item>
              <div
                style={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <OpForm.Item
                  name="locations"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <OpForm.Select
                    mode="multiple"
                    placeholder={t("kiosk.form.select")}
                    options={locations
                      .filter((location) => location.id !== defaultLocationId) // Exclude defaultLocationId
                      .map((location) => ({
                        label: location.name,
                        value: location.id,
                      }))}
                    value={optionsForm
                      .getFieldValue("locations")
                      ?.filter((id: number) => id !== defaultLocationId)}
                    onChange={() => {
                      const currentLocations =
                        optionsForm.getFieldValue("locations") || [];
                      const filteredLocations = currentLocations.filter(
                        (id: number) => id !== defaultLocationId,
                      );
                      optionsForm.setFieldsValue({
                        locations: filteredLocations,
                      });
                    }}
                  />
                </OpForm.Item>
              </div>
            </List.Item>
          )}
        </List>
      </OpForm>
    </>
  );
};

export default OptionsContent;
